/** @format */

/* eslint @typescript-eslint/dot-notation: "warn" -- Legacy code: do not use as an example. */
import Cookie from 'js-cookie';
import { NextRouter } from 'next/router';
import pRetry from 'p-retry';
import Qs from 'qs';

import { getConfig } from '@toucan/common/services/nextjs/settings';

import { DataLayerEventType } from '@toucan/client/global/analytics';
import { centToDecimal } from '@toucan/client/global/currency';
import { Model } from '@toucan/client/global/model';
import { CookieConsentCategories } from '@toucan/client/global/one-trust';
import { ProductSummaryV2 } from '@toucan/client/global/product';
import { ProductItemSummary } from '@toucan/client/global/transaction';

/** @format */

// eslint-disable-next-line @mpb-com/mpb/require-types-file, @typescript-eslint/no-explicit-any -- Legacy code: do not use as an example.
export type WindowWithDataLayer = Window & { dataLayer?: any[] };

// eslint-disable-next-line @mpb-com/mpb/require-constants-file -- Legacy code: do not use as an example.
export enum AddressTypeToDataLayer {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    'use-address-book' = 'ADDRESS_BOOK',
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    'use-new-address' = 'NEW',
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    'use-dhl-packstation' = 'NEW_PACKSTATION',
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    'use-billing-address' = 'SAME_AS_BILLING',
}

// eslint-disable-next-line @mpb-com/mpb/require-types-file, @typescript-eslint/no-explicit-any -- Legacy code: do not use as an example.
export type DataLayerPayload = Record<string, any>;

// eslint-disable-next-line @mpb-com/mpb/require-types-file -- Legacy code: do not use as an example.
export type ExperimentDataLayerEvent = {
    event: string;
    source: string;
    experimentId: string;
    experimentName: string;
    variantId?: string;
    variantName?: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy code: do not use as an example.
} & Record<string, any>;

// eslint-disable-next-line @typescript-eslint/no-inferrable-types, @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
let consentAvailable: boolean = false;
let dataLayerQueue: DataLayerPayload[] = [];
let consentIntervalId: number;

const clickRefCookieName = 'clickRef';
const CLICK_REF_MAX_LENGTH = 36;

/**
 * Iterate over entries in the dataLayerQueue and push them to the dataLayer
 * before resetting the queue to an empty list.
 */
function processDataLayerQueue() {
    if (typeof window !== 'undefined') {
        const { dataLayer } = window as WindowWithDataLayer;

        dataLayerQueue.forEach((payload) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions -- Legacy code: do not use as an example.
            dataLayer && Array.isArray(dataLayer) && dataLayer.push(payload);
        });
        dataLayerQueue = [];
    }
}

/**
 * Tracks a payload via the dataLayer bound to window scope.
 * If this method is invoked outside of a browser the method is a noop and will
 * do nothing, likewise if no dataLayer has been initialized on the window (or
 * it is not an array) then it will also do nothing.
 *
 * @param payload
 */
export function trackViaDataLayer(payload: DataLayerPayload): void {
    if (typeof window !== 'undefined') {
        const { publicRuntimeConfig } = getConfig();

        dataLayerQueue.push({
            ...payload,
            buildVersion: publicRuntimeConfig?.buildVersion,
        });

        // If consent is available, process the queue immediately.
        if (consentAvailable) {
            processDataLayerQueue();
            return;
        }

        // If no consent timer has been started, start one.
        if (!consentIntervalId) {
            waitForConsent();
        }
    }
}

export function trackEventsViaDataLayer(payloads: DataLayerPayload[]): void {
    payloads.forEach((payload) => trackViaDataLayer(payload));
}

/**
 * Track a payload via the dataLayer, specifically for enhanced ecommerce, by
 * pushing two events to the dataLayer, the first to reset data correctly as
 * per Google's recommended guide.
 *
 * @param payload
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Legacy code: do not use as an example.
export function trackEnhancedEcommerce(payload: Record<string, any>): void {
    trackViaDataLayer({
        ecommerce: null,
    });
    trackViaDataLayer(payload);
}

/**
 * Takes an array of items to buy, and transforms it into an array
 * accepted by Google's Enhanced e-commerce tracking.
 *
 * @param itemsToBuy
 * @param productMetadata
 */
export function itemsBuyingToEnhancedEcommerce(
    itemsToBuy: ProductItemSummary[],
    productMetadata: ProductSummaryV2[]
) {
    return itemsToBuy.map((product) => {
        const productData = productMetadata.find(
            (metaProduct) => metaProduct.id === product.productId
        );

        return {
            quantity: 1,
            price: centToDecimal(product.value),
            id: product.productId,
            name: productData?.modelName ?? '',
        };
    });
}

/**
 * Takes an array of items to buy, and transforms it into an array
 * to be pushed to GTM's data layer. NOTE: This format differs from the format
 * provided to enhanced ecommerce above.
 *
 * @deprecated use itemsToDataLayer instead, this format does not support GA4
 * @param itemsToBuy
 * @param productMetadata
 */
export function itemsBuyingToDataLayer(
    itemsToBuy: ProductItemSummary[],
    productMetadata: ProductSummaryV2[]
) {
    return itemsToBuy.map((product) => {
        const productData = productMetadata.find(
            (metaProduct) => metaProduct.id === product.productId
        );

        return {
            modelId: product.modelId,
            modelName: productData?.name,
            condition: product.condition,
            productId: product.productId,
            productSku: productData?.fullSku ?? '',
            productPrice: centToDecimal(product.value),
            productCategory: productData?.primaryCategoryName,
        };
    });
}

type TransformItemToDataLayerItemProps = {
    value?: number;
    id?: number;
    name?: string;
    currency?: string;
    category?: string;
    condition?: string;
    brand?: string;
    image?: string;
};

/**
 * Transforms into a data layer item that supports GA4
 */
export function transformItemToDataLayerItem({
    id,
    name,
    currency,
    category,
    value,
    condition,
    brand,
    image,
}: TransformItemToDataLayerItemProps) {
    return {
        item_id: id,
        item_name: name,
        currency,
        item_category: category,
        price: centToDecimal(value ?? 0),
        condition,
        item_brand: brand,
        product_image_url: image,
    };
}

/**
 * Takes an array of items to buy, and transforms it into an array
 * to be pushed to GTM's data layer
 */
export function itemsToDataLayer(
    items: ProductItemSummary[],
    productMetadata: ProductSummaryV2[]
) {
    return items.map((product) => {
        const productData = productMetadata.find(
            (metaProduct) => metaProduct.id === product.productId
        );
        const image = productData?.images[0]?.imageUrl;

        return transformItemToDataLayerItem({
            id: productData?.id,
            name: productData?.name,
            currency: product.currency,
            category: productData?.primaryCategoryName,
            value: product.value,
            condition: product.condition,
            image,
        });
    });
}

/**
 * Takes an array of items to sell, and transforms it into an array
 * to be pushed to GTM's data layer.
 */
export function itemsSellingToDataLayer(
    itemsToSell: ProductItemSummary[],
    modelMetadata: Model[]
) {
    return itemsToSell.map((product) => {
        const productData = modelMetadata.find(
            (metaModel) => metaModel.id === product.modelId
        );
        const productImageUrl = productData?.images[0]?.imageUrl;

        return {
            modelId: product.modelId,
            modelName: product.description,
            condition: product.condition,
            productImageUrl,
        };
    });
}

export function featureFlagToDataLayer(
    name: string,
    variant?: string,
    meta: Record<string, unknown> = {}
): ExperimentDataLayerEvent {
    const event: ExperimentDataLayerEvent = {
        event: DataLayerEventType.EXPERIMENT,
        source: 'Unleash',
        experimentId: name,
        experimentName: name,
    };

    if (variant && variant !== 'disabled') {
        event.variantId = variant;
        event.variantName = variant;
    }

    return {
        ...event,
        ...meta,
    };
}

/**
 * Waits for OptanonActiveGroups to exist within the dataLayer, denoting that
 * Cookie Consent has initialised, before invoking the provided callback function.
 *
 * @param timeout
 */
// eslint-disable-next-line @typescript-eslint/no-inferrable-types -- Legacy code: do not use as an example.
function waitForConsent(timeout: number = 4000) {
    if (typeof window !== 'undefined') {
        const tick = 100;
        const retryCount = Math.floor(timeout / tick);
        let attempt = 1;

        // Checks against a specific entry in the dataLayer with a key matching the cookie
        // consent setting populated by OneTrust
        // eslint-disable-next-line @mpb-com/mpb/boolean-function-prefix -- Legacy code: do not use as an example.
        const matcher = (): boolean => {
            const { dataLayer = [] } = window as WindowWithDataLayer;
            return dataLayer?.some(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
                (entry) => typeof entry['OptanonActiveGroups'] !== 'undefined'
            );
        };

        // Exit early if we can
        if (matcher()) {
            consentAvailable = true;
            processDataLayerQueue();
            return;
        }

        consentIntervalId = window.setInterval(() => {
            if (attempt < retryCount) {
                attempt++;
                if (matcher()) {
                    consentAvailable = true;
                    window.clearInterval(consentIntervalId);
                    processDataLayerQueue();
                }
            } else {
                // We've hit the timeout here, process the queue either way
                window.clearInterval(consentIntervalId);
                processDataLayerQueue();
            }
        }, tick);
    }
}

export function resetConsentAvailable() {
    consentAvailable = false;
}

function checkIsAlertBoxClosed() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
    const isAlertBoxClosed = window?.OneTrust?.IsAlertBoxClosed();
    if (typeof isAlertBoxClosed === 'undefined') {
        return Promise.reject(
            new Error('window.OneTrust.IsAlertBoxClosed undefined')
        );
    }

    return Promise.resolve(Boolean(isAlertBoxClosed));
}

function setClickRefIfConsentGiven(stringifiedClickRef: string) {
    // clickRef is the reference for affiliate links. It needs to be sent
    // to the backend as "click_ref" for all new checkouts/quotes.
    const hasPerformanceCookieConsent = window?.OnetrustActiveGroups?.includes(
        `,${CookieConsentCategories.PERFORMANCE},`
    );

    if (hasPerformanceCookieConsent) {
        Cookie.set(clickRefCookieName, stringifiedClickRef, { expires: 7 });
    }
}

async function attemptToSetClickRef(stringifiedClickRef: string) {
    try {
        const isAlertBoxClosed = await pRetry(checkIsAlertBoxClosed, {
            retries: 5,
            minTimeout: 2000,
        });

        if (isAlertBoxClosed) {
            setClickRefIfConsentGiven(stringifiedClickRef);
        } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- Legacy code: do not use as an example.
            window.OneTrust.OnConsentChanged(() =>
                setClickRefIfConsentGiven(stringifiedClickRef)
            );
        }
    } catch (error) {
        console.error('Failed to set clickRef cookie: ', error);
    }
}

export async function setCookies(router?: NextRouter) {
    if (!router) return;

    const { utm_source, clickref: clickRef } = Qs.parse(
        router.asPath?.replace(/^[^?]*/, ''),
        {
            ignoreQueryPrefix: true,
        }
    );
    // eslint-disable-next-line @typescript-eslint/no-base-to-string -- Legacy code: do not use as an example.
    const stringifiedUtmSource = utm_source?.toString();

    // If there's a value for utm_source, then update it in a cookie
    // otherwise do nothing. The cookie persists for 7 days
    if (stringifiedUtmSource) {
        Cookie.set('mostRecentUtmSource', stringifiedUtmSource, {
            expires: 7,
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-base-to-string -- Legacy code: do not use as an example.
    const stringifiedClickRef = clickRef?.toString();
    if (stringifiedClickRef) {
        await attemptToSetClickRef(stringifiedClickRef);
    }
}

export const getClickRef = (): string | undefined => {
    return Cookie.get(clickRefCookieName)?.slice(0, CLICK_REF_MAX_LENGTH);
};
